import { Injectable, OnDestroy } from '@angular/core';
import { MapStoreService } from '@app/energy-map/services/map-store.service';
import { MapService } from '@app/energy-map/services/map.service';
import { AuthService } from '@app/shared/services/auth.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { EfficiencyAnalysisMarkerService } from '@app/energy-map/services/efficiency-analysis-marker.service';
import { delay, first, map, takeUntil } from 'rxjs/operators';
import { CurrentUserPreferences, EnergyMapSettingsKeys, UserSettings } from '@app/energy-map/models/user-preferences';
import { UserPreferencesService } from '@app/shared/services/api/user-preferences.service';
import { isJsonString } from '@app/shared/utility';

@Injectable({
  providedIn: 'root',
})
export class NewLegendsService implements OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  public _isConsumersVisible$ = new BehaviorSubject<boolean>(true);
  public _isProducersVisible$ = new BehaviorSubject<boolean>(true);
  public _isPhotovoltaikVisible$ = new BehaviorSubject<boolean>(true);
  public _isWindVisible$ = new BehaviorSubject<boolean>(true);
  public _isBiomasseVisible$ = new BehaviorSubject<boolean>(true);
  public _isWasserVisible$ = new BehaviorSubject<boolean>(true);
  private _availableProducers$ = new BehaviorSubject<string[]>([]);
  public availableProducers$ = this._availableProducers$.asObservable();
  public _isStreetLightsVisible$ = new BehaviorSubject<boolean>(true);
  public _isStreetLightsHeatmap$ = new BehaviorSubject<boolean>(false);
  public _isIotVisible$ = new BehaviorSubject<boolean>(false);
  public _isConsumerEfficiencyVisible$ = new BehaviorSubject<boolean>(true);
  private _isZoomLevelDetailed = false;
  private readonly _detailedZoomLevel = 17;
  private _zoomLevel: any;
  private currentUserPreferences: CurrentUserPreferences = {} as CurrentUserPreferences;

  constructor(
    private authService: AuthService,
    private efficiencyAnalysisMarker: EfficiencyAnalysisMarkerService,
    private mapService: MapService,
    private mapStoreService: MapStoreService,
    private userPreferencesService: UserPreferencesService
  ) {
    this.changeStreetLightIconOnZoom();
    this.getUserPreferences();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private getUserPreferences(): void {
    this.userPreferencesService
      .getByUserPreferences(this.authService.currentUser.guid)
      .pipe(first())
      .subscribe((response: string | null) => {
        if (!!response && isJsonString(response)) {
          const parsedUserPreferences: UserSettings = JSON.parse(response);

          this._isConsumersVisible$.next(parsedUserPreferences.energyMap.isConsumersVisible);
          this._isProducersVisible$.next(parsedUserPreferences.energyMap.isProducersVisible);
          this._isStreetLightsVisible$.next(parsedUserPreferences.energyMap.isStreetLightsVisible);
          this._isStreetLightsHeatmap$.next(parsedUserPreferences.energyMap.isStreetLightsHeatmap);
          this._isIotVisible$.next(parsedUserPreferences.energyMap.isIotVisible);
          this._isConsumerEfficiencyVisible$.next(parsedUserPreferences.energyMap.isConsumerEfficiencyVisible);
          this._isPhotovoltaikVisible$.next(parsedUserPreferences.energyMap.isPhotovoltaikVisible);
          this._isBiomasseVisible$.next(parsedUserPreferences.energyMap.isBiomasseVisible);
          this._isWasserVisible$.next(parsedUserPreferences.energyMap.isWasserVisible);
          this._isWindVisible$.next(parsedUserPreferences.energyMap.isWindVisible);
        }

        this.currentUserPreferences = {
          uiPreferences: {
            energyMap: {
              isConsumersVisible: this.isConsumersVisible,
              isProducersVisible: this.isProducersVisible,
              isStreetLightsVisible: this.isStreetLightsVisible,
              isStreetLightsHeatmap: this.isStreetLightsHeatmap,
              isIotVisible: this.isIotVisible,
              isConsumerEfficiencyVisible: this.isConsumerEfficiencyVisible,
              isPhotovoltaikVisible: this.isPhotovoltaikVisible,
              isBiomasseVisible: this.isBiomasseVisible,
              isWasserVisible: this.isWasserVisible,
              isWindVisible: this.isWindVisible,
            },
          },
        };
      });
  }

  private changeStreetLightIconOnZoom = () => {
    this.mapService.zoomLevel$
      .pipe(
        map((x) => {
          this._zoomLevel = x;
          if (this.isStreetLightsHeatmap) {
            if (this.canChangeHeatMapIconWithZoomLevel(x, this._detailedZoomLevel)) {
              this.useHeatMapStreetlightMarkerWithBorder();
            } else {
              this.useHeatMapStreetlightMarkerWithoutBorder();
            }
          }
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe();
  };

  private canChangeHeatMapIconWithZoomLevel = (zoomLevel: any, allowedZoomLevel: any) => zoomLevel > allowedZoomLevel;

  public get isConsumersVisible$(): Observable<boolean> {
    return this._isConsumersVisible$.asObservable();
  }
  public get isConsumersVisible(): boolean {
    return this._isConsumersVisible$.value;
  }

  public get isConsumerEfficiencyVisible$(): Observable<boolean> {
    return this._isConsumerEfficiencyVisible$.asObservable();
  }
  public get isConsumerEfficiencyVisible(): boolean {
    return this._isConsumerEfficiencyVisible$.value;
  }

  public get isProducersVisible$(): Observable<boolean> {
    return this._isProducersVisible$.asObservable();
  }
  public get isPhotovoltaikVisible$(): Observable<boolean> {
    return this._isProducersVisible$.asObservable();
  }
  public get isWindVisible$(): Observable<boolean> {
    return this._isProducersVisible$.asObservable();
  }
  public get isBiomasseVisible$(): Observable<boolean> {
    return this._isProducersVisible$.asObservable();
  }
  public get isWasserVisible$(): Observable<boolean> {
    return this._isProducersVisible$.asObservable();
  }
  public get isProducersVisible(): boolean {
    return this._isProducersVisible$.value;
  }
  public get isPhotovoltaikVisible(): boolean {
    return this._isPhotovoltaikVisible$.value;
  }
  public get isWindVisible(): boolean {
    return this._isWindVisible$.value;
  }
  public get isBiomasseVisible(): boolean {
    return this._isBiomasseVisible$.value;
  }
  public get isWasserVisible(): boolean {
    return this._isWasserVisible$.value;
  }

  public get availableProducers(): string[] {
    return this._availableProducers$.value;
  }

  public updateAvailableProducers(types: string[]): void {
    this._availableProducers$.next(types);
  }

  public get isStreetLightsVisible$(): Observable<boolean> {
    return this._isStreetLightsVisible$.asObservable();
  }
  public get isStreetLightsVisible(): boolean {
    return this._isStreetLightsVisible$.value;
  }

  public get isStreetLightsHeatmap$(): Observable<boolean> {
    return this._isStreetLightsHeatmap$.asObservable();
  }
  public get isStreetLightsHeatmap(): boolean {
    return this._isStreetLightsHeatmap$.value;
  }

  public get isIotVisible$(): Observable<boolean> {
    return this._isIotVisible$.asObservable();
  }
  public get isIotVisible(): boolean {
    return this._isIotVisible$.value;
  }

  public updatePreferences(energyMapSettings: Partial<EnergyMapSettingsKeys>): void {
    this.currentUserPreferences.uiPreferences.energyMap = {
      ...this.currentUserPreferences.uiPreferences.energyMap,
      ...energyMapSettings,
    };
    const initialUserPreferences = this.currentUserPreferences.uiPreferences;
    const initialStringify = JSON.stringify(initialUserPreferences);
    const newUserPreferences = {
      uiPreferences: initialStringify,
    };
    this.userPreferencesService.patchByUserPreferences(this.authService.currentUser.guid, newUserPreferences).pipe(delay(500), first()).subscribe();
  }

  public toggleConsumersLayer() {
    (this.mapStoreService.map as L.Map).removeLayer(this.mapStoreService.consumerCluster);

    if (this.isConsumersVisible && this.authService.permissions.ENERGY_MAP_CONSUMERS) {
      (this.mapStoreService.map as L.Map).addLayer(this.mapStoreService.consumerCluster);
    }
  }

  public toggleAllProducerLayer(): void {
    const clusters = [
      this.mapStoreService.photovoltaicCluster,
      this.mapStoreService.biomasseCluster,
      this.mapStoreService.waterCluster,
      this.mapStoreService.windCluster,
    ];

    this._isPhotovoltaikVisible$.next(this.isProducersVisible);
    this._isBiomasseVisible$.next(this.isProducersVisible);
    this._isWasserVisible$.next(this.isProducersVisible);
    this._isWindVisible$.next(this.isProducersVisible);

    clusters.forEach((cluster) => {
      if (cluster) {
        this.isProducersVisible ? (this.mapStoreService.map as L.Map).addLayer(cluster) : (this.mapStoreService.map as L.Map).removeLayer(cluster);
      }
    });
  }

  public toggleProducerLayer(isVisible: boolean, energyType: string) {
    let cluster: any = null;

    switch (energyType) {
      case 'Photovoltaik':
        cluster = this.mapStoreService.photovoltaicCluster;
        break;
      case 'Biomasse':
        cluster = this.mapStoreService.biomasseCluster;
        break;
      case 'Wasser':
        cluster = this.mapStoreService.waterCluster;
        break;
      case 'Wind':
        cluster = this.mapStoreService.windCluster;
        break;
    }

    if (isVisible) {
      (this.mapStoreService.map as L.Map).addLayer(cluster);
    } else {
      (this.mapStoreService.map as L.Map).removeLayer(cluster);
    }
  }

  public toggleConsumerEfficiencyLayer() {
    this.efficiencyAnalysisMarker.setEfficiencyAnalysisDisabledValue(!this.isConsumerEfficiencyVisible);
  }

  public toggleStreetlightsLayer() {
    this.removeStreetLightLayer();

    if (this.isStreetLightsVisible && this.isStreetLightsHeatmap) {
      const currentZoom = (this.mapStoreService.map as L.Map).getZoom();

      if (this.canChangeHeatMapIconWithZoomLevel(currentZoom, this._detailedZoomLevel)) {
        this.mapStreetLightMarkers();
        (this.mapStoreService.map as L.Map).addLayer(this.mapStoreService.streetLightCluster);
      } else {
        this.mapHeatMarkers();
        (this.mapStoreService.map as L.Map).addLayer(this.mapStoreService.streetLightHeatmap);
      }
    }

    if (this.isStreetLightsVisible && !this.isStreetLightsHeatmap) {
      this.mapStreetLightMarkers();
      (this.mapStoreService.map as L.Map).addLayer(this.mapStoreService.streetLightCluster);
    }
  }

  removeStreetLightLayer = () => {
    (this.mapStoreService.map as L.Map).removeLayer(this.mapStoreService.streetLightCluster);
    (this.mapStoreService.map as L.Map).removeLayer(this.mapStoreService.streetLightHeatmap);
  };

  mapHeatMarkers = () => {
    this.mapStoreService.lampMarkers.forEach((lamp) => {
      this.mapService.updateLampMarker(lamp, {
        isSelected: lamp.options.isSelected,
        isHighlighted: lamp.options.isHighlighted,
        isHeatmap: this.canChangeHeatMapIconWithZoomLevel(this._zoomLevel, this._isZoomLevelDetailed),
      });
    });
  };

  mapStreetLightMarkers = () => {
    this.mapStoreService.lampMarkers.forEach((lamp) => {
      this.mapService.updateLampMarker(lamp, {
        isSelected: lamp.options.isSelected,
        isHighlighted: lamp.options.isHighlighted,
        isHeatmap: false,
      });
    });
  };

  useHeatMapStreetlightMarkerWithBorder = () => {
    this.removeStreetLightLayer();
    this.mapStreetLightMarkers();
    (this.mapStoreService.map as L.Map).addLayer(this.mapStoreService.streetLightHeatmap);
  };

  useHeatMapStreetlightMarkerWithoutBorder = () => {
    this.removeStreetLightLayer();
    this.mapHeatMarkers();
    (this.mapStoreService.map as L.Map).addLayer(this.mapStoreService.streetLightHeatmap);
  };
}
